import { carregarJson, construirIntervencao, intervencaoMuda, salvarJson, simularRodada, type EstadoJogo, type IntervencaoPartida } from '../core';
import { app, humano, jogoAtual } from './app';
import { formacaoParaEnvio } from './pausa';

/**
 * Substituições e mudanças de tática com a partida do jogo solo em andamento, como no online: o técnico pausa, mexe no time e a
 * rodada é refeita a partir do estado de ANTES dela, com as intervenções dele. Cada jogo tem o próprio sorteio, então só a partida
 * do técnico muda; o que já passou na tela (até o minuto da pausa) continua igual, porque a simulação é determinística.
 */

let snapshot: string | null = null;
let intervencoes: Record<number, IntervencaoPartida[]> = {};

/** Guarda o estado de antes da rodada (chame logo antes de `simularRodada`). */
export function guardarEstadoDaRodada(e: EstadoJogo): void {
  snapshot = salvarJson(e);
  intervencoes = {};
}

/** A partida atual permite mexer no time (a rodada foi guardada e o técnico joga nela)? */
export function podeSubstituir(): boolean {
  const p = app.partida;
  return Boolean(snapshot && p && !p.online && !p.espectador && p.fase !== 'fim' && p.fase !== 'penaltis');
}

export interface TaticaEmJogo {
  estilo: string;
  marcacao: string;
  ataque: string;
  passe: string;
  pressao: string;
}

/** A tática que o time tem agora na partida: a do clube, mudada pelas pausas anteriores desta rodada. */
export function taticaEmJogo(): TaticaEmJogo {
  const c = humano();
  const t: TaticaEmJogo = { estilo: c.tatica.estilo, marcacao: c.tatica.marcacao, ataque: c.tatica.ataque, passe: c.tatica.passe ?? 'misto', pressao: c.tatica.pressao ?? 'media' };
  for (const iv of intervencoes[c.id] ?? []) {
    if (iv.estilo) t.estilo = iv.estilo;
    if (iv.marcacao) t.marcacao = iv.marcacao;
    if (iv.ataque) t.ataque = iv.ataque;
    if (iv.passe) t.passe = iv.passe;
    if (iv.pressao) t.pressao = iv.pressao;
  }
  return t;
}

/**
 * Aplica o que o técnico pediu na janela de pausa. Devolve o erro (texto) se o pedido não vale, ou null. Se algo mudou, a rodada é
 * refeita e a tela passa a mostrar a partida nova (o minuto atual não muda).
 */
export function confirmarSubstituicoes(): string | null {
  const st = app.pausa;
  const p = app.partida;
  if (!st || !p || !snapshot) return null;
  const e = jogoAtual();
  const clube = humano();
  const partida = e.ultimaRodada?.partidas.find((x) => x.mandante === clube.id || x.visitante === clube.id);
  if (!partida) return 'sua partida não foi encontrada';
  const atual = taticaEmJogo();
  const formacao = formacaoParaEnvio(st);
  // só o que mudou: repetir a tática de antes não conta como mudança (e não refaz a rodada à toa)
  const igual = (a: string, b: string): string | undefined => (a === b ? undefined : a);
  let iv: IntervencaoPartida;
  try {
    iv = construirIntervencao(clube, partida, st.minuto, {
      subs: st.pendentes,
      estilo: igual(st.estilo, atual.estilo),
      marcacao: igual(st.marcacao, atual.marcacao),
      ataque: igual(st.ataque, atual.ataque),
      passe: igual(st.passe, atual.passe),
      pressao: igual(st.pressao, atual.pressao),
      ...(formacao ? { formacao } : {}),
    });
  } catch (erro) {
    return erro instanceof Error ? erro.message : 'mudança inválida';
  }
  if (!intervencaoMuda(iv)) return null;
  (intervencoes[clube.id] ??= []).push(iv);
  const novo = carregarJson(snapshot);
  const rodada = simularRodada(novo, intervencoes);
  app.jogo = novo;
  p.rodada = rodada;
  return null;
}
